import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import * as gameApi from '../api/gameApi';

const GameContext = createContext(null);

export function GameProvider({ children }) {
  const [currentGame, setCurrentGame] = useState(null);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const lastControlJsonRef = useRef(null);

  const clearError = useCallback(() => setError(null), []);

  const withLoading = useCallback(async (fn) => {
    setLoading(true);
    setError(null);
    try {
      const result = await fn();
      return result;
    } catch (err) {
      const message = err.response?.data?.detail || err.response?.data?.message || err.message;
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchGames = useCallback(async (params) => {
    return withLoading(async () => {
      const data = await gameApi.getGames(params);
      setGames(data.games || data);
      return data;
    });
  }, [withLoading]);

  const createGame = useCallback(async (data) => {
    return withLoading(async () => {
      const result = await gameApi.createGame(data);
      setCurrentGame(result.game || result);
      return result;
    });
  }, [withLoading]);

  const fetchGameState = useCallback(async (gameId) => {
    try {
      const data = await gameApi.getGameState(gameId);
      setCurrentGame(data.game || data);
      return data;
    } catch (err) {
      const message = err.response?.data?.detail || err.message;
      setError(message);
      throw err;
    }
  }, []);

  const fetchGameControl = useCallback(async (gameId, { skipIfUnchanged = false } = {}) => {
    try {
      const data = await gameApi.getGameControl(gameId);
      if (skipIfUnchanged) {
        const json = JSON.stringify(data);
        if (json === lastControlJsonRef.current) return data;
        lastControlJsonRef.current = json;
      }
      setCurrentGame(data.game || data);
      return data;
    } catch (err) {
      const message = err.response?.data?.detail || err.message;
      setError(message);
      throw err;
    }
  }, []);

  const selectBox = useCallback(async (gameId, boxNumber) => {
    return withLoading(async () => {
      const result = await gameApi.selectBox(gameId, boxNumber);
      setCurrentGame(result.game || result);
      return result;
    });
  }, [withLoading]);

  const openBox = useCallback(async (gameId, boxNumber) => {
    return withLoading(async () => {
      const result = await gameApi.openBox(gameId, boxNumber);
      setCurrentGame(result.game || result);
      return result;
    });
  }, [withLoading]);

  const advanceRound = useCallback(async (gameId) => {
    return withLoading(async () => {
      const result = await gameApi.advanceRound(gameId);
      setCurrentGame(result.game || result);
      return result;
    });
  }, [withLoading]);

  const makeOffer = useCallback(async (gameId, data) => {
    return withLoading(async () => {
      const result = await gameApi.makeOffer(gameId, data);
      setCurrentGame(result.game || result);
      return result;
    });
  }, [withLoading]);

  const respondOffer = useCallback(async (gameId, accepted) => {
    return withLoading(async () => {
      const result = await gameApi.respondOffer(gameId, accepted);
      setCurrentGame(result.game || result);
      return result;
    });
  }, [withLoading]);

  const completeGame = useCallback(async (gameId, data) => {
    return withLoading(async () => {
      const result = await gameApi.completeGame(gameId, data);
      setCurrentGame(result.game || result);
      return result;
    });
  }, [withLoading]);

  const fetchHistory = useCallback(async (params) => {
    return withLoading(async () => {
      const data = await gameApi.getHistory(params);
      return data;
    });
  }, [withLoading]);

  const value = {
    currentGame,
    games,
    loading,
    error,
    clearError,
    fetchGames,
    createGame,
    fetchGameState,
    fetchGameControl,
    selectBox,
    openBox,
    advanceRound,
    makeOffer,
    respondOffer,
    completeGame,
    fetchHistory,
    setCurrentGame,
  };

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}

export function useGame() {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
}

export default GameContext;
